import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { MessageBox, MessageBoxType } from 'record-it-components/misc/MessageBox/MessageBox';
import { PageContentContainer, PageContentContainerProps } from 'record-it-components/pageContentLayout/PageContentContainer/PageContentContainer';
import { PageContentHeader } from 'record-it-components/pageContentLayout/PageContentHeader/PageContentHeader';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { LatestEntityContainer } from '../../../classes/entityUtils/LatestEntityContainer/LatestEntityContainer';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { CreateConstructionSiteForm, CreateConstructionSiteFormProps } from '../CreateConstructionSiteForm/CreateConstructionSiteForm';
export type CreateConstructionSitePageContentProps = PropsWithoutChildren<PageContentContainerProps> & {
  userGroupId: string;
  onConstructionSiteCreated: (constructionSiteContainer: LatestEntityContainer<EntityName.ConstructionSite>) => void;
  onCancel: () => void;
};
export const CreateConstructionSitePageContent: FunctionComponent<CreateConstructionSitePageContentProps> = React.memo(props => {
  const $ = _c(25);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let onCancel;
  let onConstructionSiteCreated;
  let remainingProps;
  let userGroupId;
  if ($[2] !== t0) {
    ({
      userGroupId,
      onConstructionSiteCreated,
      onCancel,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = onCancel;
    $[4] = onConstructionSiteCreated;
    $[5] = remainingProps;
    $[6] = userGroupId;
  } else {
    onCancel = $[3];
    onConstructionSiteCreated = $[4];
    remainingProps = $[5];
    userGroupId = $[6];
  }
  const constructionSiteRepository = useEntityRepository(EntityName.ConstructionSite);
  const compilations = useEntities(EntityName.TimeTrackingActivityCompilation, "getByUserGroupId", userGroupId);
  const defaultCompilation = compilations[0] ?? null;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[7] !== constructionSiteRepository || $[8] !== onConstructionSiteCreated || $[9] !== userGroupId) {
    t1 = constructionSiteToCreate => {
      const constructionSite = constructionSiteRepository.create({
        ...constructionSiteToCreate,
        ownerUserGroupId: userGroupId
      });
      onConstructionSiteCreated(new LatestEntityContainer(constructionSite.id, constructionSiteRepository));
    };
    $[7] = constructionSiteRepository;
    $[8] = onConstructionSiteCreated;
    $[9] = userGroupId;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const handleCreateConstructionSite = useEventCallback(t1);
  let t2;
  if ($[11] !== t) {
    t2 = t("components.constructionSite.CreateConstructionSitePageContent.title");
    $[11] = t;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== t2) {
    t3 = <PageContentHeader title={t2} iconConfig={iconConfigurations.prefixed.page.CreateConstructionSitePage.titleIcon} />;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== defaultCompilation || $[16] !== handleCreateConstructionSite || $[17] !== onCancel || $[18] !== t || $[19] !== userGroupId) {
    t4 = defaultCompilation ? <CreateConstructionSiteForm userGroupId={userGroupId} defaultTimeTrackingActivityCompilationId={defaultCompilation.id} onCreateConstructionSite={handleCreateConstructionSite} onCancel={onCancel} data-testid="CreateConstructionSitePageContent.Form" /> : <MessageBox type={MessageBoxType.Error} data-testid="CreateConstructionSitePageContent.NoCompilationMessageBox">{t("components.constructionSite.CreateConstructionSitePageContent.noCompilationFound")}</MessageBox>;
    $[15] = defaultCompilation;
    $[16] = handleCreateConstructionSite;
    $[17] = onCancel;
    $[18] = t;
    $[19] = userGroupId;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== remainingProps || $[22] !== t3 || $[23] !== t4) {
    t5 = <PageContentContainer {...remainingProps}>{t3}{t4}</PageContentContainer>;
    $[21] = remainingProps;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return t5;
});